import { Cancel01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Form, Formik } from "formik";
import { Button, Drawer, FormControlLabel, IconButton, Paper, Stack, Switch, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { DraftMenu, Flow, Menu } from "@/types/menus";
import { useAppSelector } from "@/hooks";
import { MediaManager } from "@/components";
import { useAddMenuMutation } from "@/features/organizations/api/menusApi";
import useSnackbar from "@/hooks/useSnackbar";
import * as Yup from "yup";
import InputText from "@/components/InputText";
import LoadingButton from "@mui/lab/LoadingButton";
import { useAccessControl } from "@/features/access-control";

const FormSchema = Yup.object().shape({
  title: Yup.string().min(3, "Muy corto, ingresa al menos 3 caracteres").max(50, "Muy largo, máximo 150 caracteres").required("Campo requerido"),
  description: Yup.string().min(15, "Muy corto, ingresa al menos 50 caracteres").max(150, "Muy largo, máximo 150 caracteres").required("Campo requerido"),
  image: Yup.array().of(Yup.string()).min(1, "La foto de portada es requerida").max(1).required("Campo requerido"),
  organization: Yup.string(),
  flow: Yup.array().of(Yup.string()),
  isPublished: Yup.boolean(),
});

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const MenuSettings = ({ isOpen, onClose }: Props) => {
  const [openSnackbar] = useSnackbar();
  const { hasAccess } = useAccessControl();
  const [addMenu, { isLoading }] = useAddMenuMutation();
  const author = useAppSelector((state) => state.auth.user.uid);
  const { organizationId } = useParams<{ organizationId: string }>();

  return (
    <Drawer open={Boolean(isOpen && organizationId)} onClose={onClose}>
      <Formik
        initialValues={{
          title: "",
          description: "",
          image: [],
          organization: organizationId,
          flow: [Flow.DINE_IN],
          isPublished: false,
          createdBy: author,
          createdAt: undefined,
        }}
        onSubmit={async (values: DraftMenu) => {
          const response = await addMenu({ draft: values, organizationId });

          if (!response?.error) {
            openSnackbar("Menú creado", "success");
            onClose();
          } else {
            openSnackbar("Error al crear el menú", "error");
          }
        }}
        validationSchema={FormSchema}
      >
        {({ values, errors, setFieldValue, handleChange }) => {
          return (
            <Form>
              <Stack direction="row" justifyContent="space-between" alignItems="center" padding={2}>
                <Typography variant="h2">Crear menú</Typography>
                <Paper
                  sx={{
                    borderRadius: "50%",
                  }}
                >
                  <IconButton onClick={onClose} color="primary">
                    <HugeiconsIcon icon={Cancel01Icon} />
                  </IconButton>
                </Paper>
              </Stack>
              <Stack gap={2} paddingX={2}>
                <Paper sx={{ padding: 2 }}>
                  <Typography variant="h4">Detalles</Typography>
                  <InputText label="Nombre" margin="normal" name="title" size="small" />
                  <InputText label="Descripción" margin="dense" name="description" size="small" multiline rows={3} />
                  <InputText label="Orden" margin="dense" name="order" size="small" type="number" />
                </Paper>
                <Paper sx={{ padding: 2 }}>
                  <Stack gap={2}>
                    <Typography variant="h4">Foto de portada</Typography>
                    <MediaManager
                      value={values?.image[0]}
                      onChange={(value: Menu["image"]) => {
                        setFieldValue("image", value);
                      }}
                      helperText={errors?.image as string | undefined}
                    />
                  </Stack>
                </Paper>
                <Paper sx={{ padding: 2 }}>
                  <Stack gap={2}>
                    <Typography variant="h4">Flujos</Typography>
                    <Stack>
                      <FormControlLabel
                        label="Dine In"
                        labelPlacement="start"
                        sx={{
                          margin: 0,
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                        control={
                          <Switch
                            name={Flow.DINE_IN}
                            checked={values.flow.includes(Flow.DINE_IN)}
                            onChange={(e: { target: { name: string; checked: boolean } }) => {
                              const index = values.flow.findIndex((f: string) => f === e.target.name);
                              const draft = [...values.flow];

                              if (index >= 0) {
                                draft.splice(index, 1);
                              } else {
                                draft.push(e.target.name as Flow);
                              }

                              setFieldValue("flow", draft);
                            }}
                          />
                        }
                      />
                      <FormControlLabel
                        label="Delivery"
                        labelPlacement="start"
                        sx={{
                          margin: 0,
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                        control={
                          <Switch
                            name={Flow.DELIVERY}
                            checked={values.flow.includes(Flow.DELIVERY)}
                            onChange={(e: { target: { name: string; checked: boolean } }) => {
                              const index = values.flow.findIndex((f: string) => f === e.target.name);
                              const draft = [...values.flow];

                              if (index >= 0) {
                                draft.splice(index, 1);
                              } else {
                                draft.push(e.target.name as Flow);
                              }

                              setFieldValue("flow", draft);
                            }}
                          />
                        }
                      />
                    </Stack>
                  </Stack>
                </Paper>
                <Paper sx={{ padding: 2 }}>
                  <Stack gap={2}>
                    <Typography variant="h4">Estado</Typography>
                    <Stack>
                      <FormControlLabel
                        label="Publicado"
                        labelPlacement="start"
                        sx={{
                          margin: 0,
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                        control={<Switch name="isPublished" checked={values.isPublished} onChange={handleChange} />}
                      />
                    </Stack>
                  </Stack>
                </Paper>
                <Stack direction="row" alignItems="center" gap={2}>
                  {hasAccess("create.menu") ? (
                    <LoadingButton variant="contained" color="success" type="submit" loading={isLoading}>
                      Crear
                    </LoadingButton>
                  ) : null}
                  <Button disabled={isLoading} variant="text" onClick={onClose}>
                    Cancelar
                  </Button>
                </Stack>
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </Drawer>
  );
};

export default MenuSettings;
